import { focusSurveyQuestion, sectionNumbers, useSurveyPreviewDraft } from "./surveyQuestions";

const CARD = "-mx-0.5 cursor-pointer rounded-2xl outline-none ring-[var(--mainColor)] transition hover:ring-2 focus-visible:ring-2";
const FIELD = "rounded-xl bg-gray-50 px-3.5 py-3 text-[14px] text-gray-400";

function AnswerPreview({ question }) {
    switch (question.type) {
        case 'SHORT_TEXT':
            return <div className={FIELD}>{question.validation?.kind === 'EMAIL' ? 'name@example.com' : question.validation?.kind === 'NUMBER' ? '숫자 입력' : '답변 입력'}</div>;
        case 'LONG_TEXT':
            return <div className={`${FIELD} h-20`}>답변 입력</div>;
        case 'DATE':
            return <div className={`${FIELD} w-40`}>연도. 월. 일</div>;
        case 'DROPDOWN':
            return <div className={`${FIELD} flex max-w-[240px] justify-between`}>선택해 주세요 <span aria-hidden="true">⌄</span></div>;
        case 'SINGLE_CHOICE':
        case 'MULTIPLE_CHOICE':
            return (
                <div className="flex flex-col gap-1.5">
                    {[...question.options, ...(question.allowOther ? [{ id: '_other', label: '기타: ______' }] : [])].map((option, i) => (
                        <div key={option.id} className="flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 ring-1 ring-gray-200">
                            <span className={`h-4 w-4 shrink-0 border-2 border-gray-300 ${question.type === 'SINGLE_CHOICE' ? 'rounded-full' : 'rounded'}`} />
                            <span className={`text-[14px] ${option.label ? 'text-gray-800' : 'text-gray-300'}`}>{option.label || `선택지 ${i + 1}`}</span>
                        </div>
                    ))}
                </div>
            );
        case 'LINEAR_SCALE': {
            const { min, max, minLabel, maxLabel } = question.scale;
            const values = Array.from({ length: max - min + 1 }, (_, i) => min + i);
            return (
                <div>
                    <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${values.length}, minmax(0, 1fr))` }}>
                        {values.map(n => <span key={n} className="flex h-9 items-center justify-center rounded-lg bg-gray-50 text-[13px] font-bold text-gray-600 ring-1 ring-gray-200">{n}</span>)}
                    </div>
                    {(minLabel || maxLabel) && <div className="mt-1.5 flex justify-between text-[11px] text-gray-500"><span>{minLabel}</span><span>{maxLabel}</span></div>}
                </div>
            );
        }
        case 'RATING':
            return <div className="text-[28px] leading-none tracking-wide text-gray-200">{'★'.repeat(question.scale.max)}</div>;
        case 'CHOICE_GRID':
        case 'CHECKBOX_GRID':
            return (
                <div className="flex flex-col gap-1.5">
                    {question.rows.map((row, i) => (
                        <div key={row.id} className="rounded-xl px-3.5 py-2.5 ring-1 ring-gray-200">
                            <p className={`text-[13px] font-bold ${row.label ? 'text-gray-800' : 'text-gray-300'}`}>{row.label || `행 ${i + 1}`}</p>
                            <div className="mt-1.5 flex flex-wrap gap-1">
                                {question.options.map((col, j) => <span key={col.id} className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ring-1 ring-gray-200 ${col.label ? 'text-gray-600' : 'text-gray-300'}`}>{col.label || `열 ${j + 1}`}</span>)}
                            </div>
                        </div>
                    ))}
                </div>
            );
        default:
            return null;
    }
}

/** 쓰는 중인 설문 (PC 오른쪽 미리보기·모바일 미리보기 공용). onSelectQuestion(id): 질문을 눌렀을 때 */
export function SurveyPreviewArticle({ draft, onSelectQuestion }) {
    if (!draft) {
        return null;
    }
    let number = 0;
    const sections = sectionNumbers(draft.questions);
    const select = (id) => ({
        role: 'button',
        tabIndex: 0,
        title: '눌러서 이 질문 고치기',
        onClick: () => onSelectQuestion(id),
        onKeyDown: (e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onSelectQuestion(id)),
    });

    return (
        <div className="flex flex-col gap-2.5">
            <div className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_28px_rgba(0,0,0,0.06)]">
                <div className="h-1.5 bg-gradient-to-r from-[#ee9ca7] via-[#b794f6] to-[#7aa2ff]" aria-hidden="true" />
                <div className="px-5 pb-5 pt-4">
                    <h1 className={`break-words text-[21px] font-extrabold leading-tight ${draft.title.trim() ? 'text-gray-900' : 'text-gray-300'}`}>{draft.title.trim() || '설문 제목'}</h1>
                    {draft.description.trim() && <p className="mt-2 whitespace-pre-line break-words text-[13px] leading-relaxed text-gray-600">{draft.description.trim()}</p>}
                </div>
            </div>

            {draft.questions.map(question => {
                if (question.type === 'PAGE') {
                    return (
                        <div key={question.id} {...select(question.id)} className={`${CARD} mt-4 overflow-hidden bg-white ring-1 ring-black/[0.04]`}>
                            <p className="bg-gray-900 px-5 py-1.5 text-[11px] font-bold text-white">섹션 {sections[question.id]} · 다음 화면</p>
                            <div className="px-5 py-3.5">
                                <p className={`break-words text-[16px] font-extrabold ${question.title ? 'text-gray-900' : 'text-gray-300'}`}>{question.title || '섹션 제목'}</p>
                                {question.description && <p className="mt-1 whitespace-pre-line break-words text-[12px] text-gray-500">{question.description}</p>}
                            </div>
                        </div>
                    );
                }
                if (question.type === 'SECTION') {
                    return (
                        <div key={question.id} {...select(question.id)} className={`${CARD} mt-2 bg-gray-900 px-5 py-4 text-white`}>
                            <p className={`break-words text-[16px] font-bold ${question.title ? '' : 'text-white/40'}`}>{question.title || '제목'}</p>
                            {question.description && <p className="mt-1 whitespace-pre-line break-words text-[12px] text-white/70">{question.description}</p>}
                        </div>
                    );
                }
                number += 1;
                return (
                    <div key={question.id} {...select(question.id)} className={`${CARD} bg-white px-5 py-4 ring-1 ring-black/[0.04]`}>
                        <p className="text-[11px] font-bold text-gray-400">Q{number}</p>
                        <p className={`mt-0.5 break-words text-[15px] font-bold ${question.title ? 'text-gray-900' : 'text-gray-300'}`}>
                            {question.title || '질문'}{question.required && <span className="ml-1 text-rose-500">*</span>}
                        </p>
                        {question.description && <p className="mt-0.5 whitespace-pre-line break-words text-[12px] text-gray-500">{question.description}</p>}
                        <div className="mt-3"><AnswerPreview question={question} /></div>
                    </div>
                );
            })}

            {draft.collectConsent &&
                <div className="rounded-2xl bg-white px-5 py-4 text-[13px] ring-1 ring-black/[0.04]">
                    <p className="font-bold text-gray-900">개인정보 수집·이용 동의 <span className="text-rose-500">*</span></p>
                    <p className="mt-1 text-[12px] text-gray-500">수집 항목·목적·받는 사람(@{draft.domainName} 운영자)·보관 기간이 안내되고, 동의해야 보낼 수 있어요.</p>
                </div>
            }

            <div className="mt-1 flex h-12 items-center justify-center rounded-2xl bg-gray-900 text-[15px] font-bold text-white">응답 보내기</div>
        </div>
    );
}

/** PC 오른쪽 설문 미리보기 (설문을 고치는 동안 FdViewer 대신). 질문을 누르면 그 편집 카드로 */
export function FdSurveyPreview() {
    const draft = useSurveyPreviewDraft();

    return (
        <aside className="fixed bottom-0 right-0 top-14 flex w-[435px] flex-col border-l border-black/[0.06] bg-[#ececf0]" aria-label="설문 미리보기">
            <div className="flex items-center justify-between px-5 py-3">
                <span className="text-xs font-bold tracking-wide text-gray-500">설문 미리보기</span>
                <span className="text-[11px] text-gray-400">누르면 그 질문을 고쳐요</span>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-8">
                {draft
                    ? <SurveyPreviewArticle draft={draft} onSelectQuestion={focusSurveyQuestion} />
                    : <p className="px-2 pt-10 text-center text-[13px] text-gray-400">응답 탭에서는 미리보기를 쉬어요.</p>}
            </div>
        </aside>
    );
}
